import { CHART_SERIES_PALETTE } from "@/shared/ui/chart/public";
import {
  groupSeriesKey,
  shortRunName,
  type CompareRun,
  type EvalResultSeries,
} from "./evalCompareModel";
import { buildMergedResult, type EvalMergedResult } from "./evalCompareMergeModel";

type EvalGroupCompareInput = {
  key: string;
  label: string;
  items: readonly CompareRun[];
  totalRuns?: number;
};

type EvalGroupCompareGroup = {
  key: string;
  label: string;
  series: EvalResultSeries;
  merged: EvalMergedResult;
  retainedSeeds: readonly string[];
};

type EvalGroupCompareResult = {
  groups: readonly EvalGroupCompareGroup[];
  series: readonly EvalResultSeries[];
  seedSetsDiffer: boolean;
};

function groupSeriesIdentity(
  key: string,
  label: string,
  index: number,
): Pick<EvalResultSeries, "key" | "color" | "label" | "shortLabel"> {
  return {
    key: groupSeriesKey(key),
    color: CHART_SERIES_PALETTE[index % CHART_SERIES_PALETTE.length],
    label,
    shortLabel: shortRunName(key),
  };
}

export function buildGroupPlaceholderSeries(
  groups: readonly { key: string; label: string; runCount: number; totalRuns?: number }[],
): EvalResultSeries[] {
  return groups.map((group, index) => ({
    ...groupSeriesIdentity(group.key, group.label, index),
    summary: {
      finalSuccessRate: null,
      durationSec: null,
      outcomeBreakdown: [],
      distributions: [],
    },
    sublabel:
      group.totalRuns !== undefined && group.totalRuns > group.runCount
        ? `최근 실행 ${group.runCount}개 / 전체 ${group.totalRuns}개`
        : `실행 ${group.runCount}개`,
  }));
}

/** 각 정책 안에서만 seed 중복을 제거해 정책별 pooled series를 만든다. */
export function buildGroupCompareResult(
  inputs: readonly EvalGroupCompareInput[],
): EvalGroupCompareResult {
  const retainedSeedsOf = (items: readonly CompareRun[]): readonly string[] =>
    [
      ...new Set(
        items
          .filter((item) => item.run.terms !== null && item.simulationSeed !== null)
          .map((item) => item.simulationSeed as string),
      ),
    ].sort();
  const groups = inputs.map((input, index) => {
    const merged = buildMergedResult(input.items);
    const totalRuns = input.totalRuns ?? input.items.length;
    const pooledTally =
      merged.includedRunCount === totalRuns
        ? `실행 ${merged.includedRunCount}개`
        : `실행 ${totalRuns}개 중 ${merged.includedRunCount}개 통합`;
    return {
      key: input.key,
      label: input.label,
      merged,
      retainedSeeds: retainedSeedsOf(input.items),
      series: {
        ...merged.series,
        ...groupSeriesIdentity(input.key, input.label, index),
        sublabel: `${pooledTally} · ${merged.series.sublabel}`,
      },
    };
  });
  const seedSets = groups
    .map((group) => group.retainedSeeds.join(","))
    .filter((seeds) => seeds !== "");
  return {
    groups,
    series: groups.map((group) => group.series),
    seedSetsDiffer: new Set(seedSets).size > 1,
  };
}
